/*
PROV: COHINT001.BF.RMA69WLMOQ
REQ: SYS-ARCH-15
WHY: Shared package module.
*/

import { getLastApiContext } from "../api/client";

type BrowserErrorReporterOptions = {
  app: string;
  endpoint?: string;
};

type ClientErrorPayload = {
  type: "error" | "unhandledrejection";
  app: string;
  app_env?: string;
  git_sha?: string;
  build_timestamp?: string;
  url?: string;
  user_agent?: string;
  message?: string;
  stack?: string;
  name?: string;
  last_request_id?: string;
  last_correlation_id?: string;
  last_trace_id?: string;
  at_utc: string;
  anon_session_id?: string;
};

function nowIso(): string {
  try {
    return new Date().toISOString();
  } catch {
    return "";
  }
}

function pickEnv(key: string): string | undefined {
  const v = (process as any)?.env?.[key];
  if (!v) return undefined;
  return String(v);
}

function buildPayload(type: ClientErrorPayload["type"], app: string, err: unknown): ClientErrorPayload {
  const ctx = getLastApiContext();
  const message = (() => {
    if (type === "unhandledrejection") {
      // Often a string or Error.
      const anyErr = err as any;
      if (anyErr?.reason instanceof Error) return anyErr.reason.message;
      if (anyErr?.reason) return String(anyErr.reason);
    }
    if (err instanceof Error) return err.message;
    return String(err);
  })();

  const stack = (() => {
    const anyErr = err as any;
    if (type === "unhandledrejection" && anyErr?.reason instanceof Error) return anyErr.reason.stack;
    if (err instanceof Error) return err.stack;
    return undefined;
  })();

  const name = (() => {
    const anyErr = err as any;
    if (type === "unhandledrejection" && anyErr?.reason instanceof Error) return anyErr.reason.name;
    if (err instanceof Error) return err.name;
    return undefined;
  })();

  return {
    type,
    app,
    app_env: pickEnv("NEXT_PUBLIC_APP_ENV"),
    git_sha: pickEnv("NEXT_PUBLIC_GIT_SHA"),
    build_timestamp: pickEnv("NEXT_PUBLIC_BUILD_TIMESTAMP"),
    url: typeof window !== "undefined" ? window.location.href : undefined,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    message,
    stack,
    name,
    last_request_id: ctx.requestId,
    last_correlation_id: ctx.correlationId,
    last_trace_id: ctx.traceId,
    at_utc: nowIso(),
    anon_session_id: (() => {
      try {
        if (typeof window === "undefined") return undefined;
        const key = "cohorta_anon_session_id";
        const existing = window.localStorage?.getItem(key);
        if (existing && existing.trim()) return existing.trim();
        const created =
          (globalThis as any)?.crypto?.randomUUID?.() || `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
        window.localStorage?.setItem(key, created);
        return created;
      } catch {
        return undefined;
      }
    })(),
  };
}

export function installBrowserErrorReporter(options: BrowserErrorReporterOptions): () => void {
  const endpoint = options.endpoint || "/api/v1/telemetry/client-error";
  const app = options.app;

  if (typeof window === "undefined") return () => {};

  // Simple rate limiting + dedupe to avoid runaway loops.
  let lastSentAt = 0;
  let sentInWindow = 0;
  let windowStart = 0;
  const recentKeys = new Set<string>();

  function shouldSend(key: string): boolean {
    const now = Date.now();
    if (now - windowStart > 60_000) {
      windowStart = now;
      sentInWindow = 0;
      recentKeys.clear();
    }
    if (recentKeys.has(key)) return false;
    if (sentInWindow >= 10) return false;
    if (now - lastSentAt < 250) return false;
    recentKeys.add(key);
    sentInWindow += 1;
    lastSentAt = now;
    return true;
  }

  async function send(type: ClientErrorPayload["type"], err: unknown): Promise<void> {
    const payload = buildPayload(type, app, err);
    const key = `${payload.type}|${payload.message || ""}|${payload.stack || ""}`.slice(0, 500);
    if (!shouldSend(key)) return;

    try {
      const body = JSON.stringify(payload);
      if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        navigator.sendBeacon(endpoint, new Blob([body], { type: "application/json" }));
        return;
      }
      await fetch(endpoint, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-correlation-id": (globalThis as any)?.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
        },
        body,
        credentials: "include",
        keepalive: true,
      });
    } catch {
      // Intentionally swallow.
    }
  }

  const onError = (event: ErrorEvent) => {
    void send("error", event.error || event.message);
  };
  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    void send("unhandledrejection", event);
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
  };
}
